"use client";
import { Link, Text } from "@softmax/design-system";
import { colors, fonts } from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import type { CoworldReplayTelemetryContext } from "../frame/replay-telemetry";
import { replayLinks, type ReplayViewerKind } from "./replay-viewer-links";
import { EmbeddedReplayViewer } from "./embedded-replay-viewer";
import { ReplayDownload } from "./replay-download";
import { ReplayCommand } from "./replay-command";
import { CopyReplayText } from "./copy-replay-text";

export type ReplayViewerProps = Readonly<{
  replayUrl: string | null | undefined;
  label?: string;
  height?: number | string;
  showExternalLink?: boolean;
  viewer?: ReplayViewerKind;
  downloadUrl?: string | null | undefined;
  onDownload?: (() => void) | undefined;
  downloadPending?: boolean;
  downloadError?: string | null | undefined;
  coworldId?: string | null;
  coworldTelemetryContext?: CoworldReplayTelemetryContext | undefined;
  fullPageHref?: string | undefined;
  coworldSurface?: "episode_detail" | "full_page_replay";
  achievement?: string | null | undefined;
}>;
const styles = stylex.create({
  viewer: { display: "flex", flexDirection: "column", gap: 8 },
  label: {
    color: colors.muted,
    fontFamily: fonts.sans,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.025em",
    textTransform: "uppercase",
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 12,
    fontSize: 14,
  },
});

export function ReplayViewerView({
  height = 720,
  showExternalLink = true,
  viewer = "mettascope",
  renderCoworldFrame,
  ...props
}: ReplayViewerProps & {
  renderCoworldFrame: (height: string) => ReactNode;
}) {
  const frameHeight =
    typeof height === "number" ? `min(${height}px, 88vh)` : height;
  const { viewerUrl, downloadHref, openLabel, command } = replayLinks({
    ...props,
    viewer,
  });

  if ((!viewerUrl && !downloadHref) || (viewer === "mettascope" && !viewerUrl))
    return (
      <Text size="small" tone="muted">
        No replay available.
      </Text>
    );

  const copyUrl =
    viewer === "bitworld" ? (viewerUrl ?? downloadHref) : viewerUrl;
  return (
    <div {...stylex.props(styles.viewer)}>
      {props.label && <div {...stylex.props(styles.label)}>{props.label}</div>}
      <EmbeddedReplayViewer
        label={props.label}
        hasCoworld={Boolean(
          props.coworldId && props.replayUrl && props.coworldTelemetryContext,
        )}
        viewer={viewer}
        viewerUrl={viewerUrl}
        frameHeight={frameHeight}
        renderCoworldFrame={renderCoworldFrame}
      />
      {showExternalLink && (
        <div {...stylex.props(styles.actions)}>
          {viewerUrl && (
            <Link
              focus="native"
              href={viewerUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {openLabel}
            </Link>
          )}
          <ReplayDownload
            onDownload={props.onDownload}
            downloadPending={props.downloadPending}
            viewer={viewer}
            downloadHref={downloadHref}
          />
          {copyUrl && (
            <CopyReplayText key={copyUrl} text={copyUrl} label="Copy Url" />
          )}
        </div>
      )}
      {props.downloadError && (
        <div role="alert">
          <Text size="small" tone="danger">
            {props.downloadError}
          </Text>
        </div>
      )}
      {command && <ReplayCommand command={command} />}
    </div>
  );
}
